// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

type State = Object;
import {
	FETCH_APPS_CCXI_DATA,
	CHECK_HAS_THOR,
	UPDATE_ENTITLED_PRODUCTS,
	UPDATE_PRODUCTS_POST_THOR_CHECK,
	FETCH_PDP_CCXI_DATA,
	FETCH_CATALOG_BANNERS_DATA,
	FETCH_FFC_ALL_VERSION_DATA,
	FETCH_DATA_FOR_APP_PICKER,
	FETCH_SINGLE_APP_PLAN_CVS_DATA,
	FETCH_FFC_APP_PICKER_DATA} from '../types';
import appsUtil from '../utils/AppsUtil';
import thorUtil from '../utils/wam/ThorUtil';
import thorCommonUtil from '../utils/wam/ThorCommonUtil';
import AppPickerUtil from '../utils/AppPicker/AppPickerUtil';
import ModelParser from '../utils/ModelParser';
const initialState = {};

export default (state: State = initialState, action: Object) => {
	switch (action.type) {
		case FETCH_APPS_CCXI_DATA:
		{
			let modelParserReturnObj = {};
			let productArr;
			let servicesArr;
			let mobileAppArr;
			const output = {};
			const entitledProducts = [];

			output.appsPanelSkeletonConfig = action.payload.appsPanelSkeletonConfig ? action.payload.appsPanelSkeletonConfig.data : null;
			output.hasThor = action.payload.hasThor;
			appsUtil.hasThor = output.hasThor;
			let appsPanelSkeletonConfig = output.appsPanelSkeletonConfig ? output.appsPanelSkeletonConfig : null;
			if (appsPanelSkeletonConfig === null || appsUtil.hasFeature('use_skeleton_fallback')) {
				// fill with local copy of json
				appsUtil.isCVSMainFallback = true;
				output.appsPanelSkeletonConfig = appsPanelSkeletonConfig = (action.payload.appsSkeletonFallback ? action.payload.appsSkeletonFallback.data : null);
			}
			// call model parser
			ModelParser.fillAppSkeletonModel(appsPanelSkeletonConfig);

			output.entitlementData = action.payload.entitlementData ? action.payload.entitlementData.data : null;

			//
			if (action.payload.ffcData && typeof action.payload.ffcData.data.channel != 'undefined') {
				const ccmChannel = action.payload.ffcData.data.channel.find(channel => channel.name === 'ccm');
				productArr = ccmChannel ? ccmChannel.products.product : undefined;
				output.cdnInfo = action.payload.ffcData.data.channel[0].cdn;

				const serviceChannel = action.payload.ffcData.data.channel.find(channel => channel.name === 'services');
				servicesArr = serviceChannel ? serviceChannel.services.service : undefined;

				const mobileAppChannel = action.payload.ffcData.data.channel.find(channel => channel.name === 'mobileApps');
				mobileAppArr = mobileAppChannel ? mobileAppChannel.mobileApps.mobileApp : undefined;
			}

			// create lineage map
			if (productArr) {
				for (let index = 0; index < productArr.length; index++) {
					ModelParser.createApplineage(productArr[index], 'desktop');
				}
			}

			if (servicesArr) {
				for (const index in servicesArr) {
					ModelParser.createApplineage(servicesArr[index], 'web');
				}
			}

			if (mobileAppArr) {
				for (const index in mobileAppArr) {
					ModelParser.createApplineage(mobileAppArr[index], 'mobile');
				}
			}

			appsUtil.isAppLineageMapInit = true;

			/* Loop through desktop products*/
			output.appsFFCProductList = null;
			if (productArr) {
				modelParserReturnObj = ModelParser.fillAppsModel(productArr,'desktop', action.payload.hasThor, action.payload.inputLocale);
				output.appsFFCProductList = modelParserReturnObj.productsList;
				entitledProducts.concat(modelParserReturnObj.entitledProducts);
			}

			/* Loop through web app services*/
			if (servicesArr) {
				modelParserReturnObj = ModelParser.fillAppsModel(servicesArr,'web', action.payload.hasThor, action.payload.inputLocale);
				appsUtil.appsFFCServiceList = modelParserReturnObj.productsList;
				entitledProducts.concat(modelParserReturnObj.entitledProducts);
			}
			/* Loop through mobile apps */
			if (mobileAppArr) {
				modelParserReturnObj = ModelParser.fillAppsModel(mobileAppArr,'mobile', action.payload.hasThor, action.payload.inputLocale);
				appsUtil.appsFFCMobileList = modelParserReturnObj.productsList;
				entitledProducts.concat(modelParserReturnObj.entitledProducts);
			}

			// use to be old design cvs productsData
			const productsData = null;
			output.productsData = productsData;

			output.entitledProducts = entitledProducts;
			appsUtil.init(output.appsFFCProductList, output.entitlementData, output.cdnInfo, output.productsData, action.payload.inputLocale);

			if (thorUtil.isConnected() && !thorCommonUtil.onceExecutedViaWAMWorkflow()) { // Case when Thor connnect occurs before FFC
				thorUtil.setCCXModelFromWAMData(thorUtil.thorProductsList);
			}

			if (!output.appsPanelSkeletonConfig || !output.appsFFCProductList) {
				output.errorType = '500';
			}

			return Object.assign({}, state, output);
		}
		case CHECK_HAS_THOR:
		{
			const output = {};
			output.hasThor = action.payload.hasThor;
			appsUtil.hasThor = output.hasThor;
			return Object.assign({}, state, output);
		}
		case UPDATE_ENTITLED_PRODUCTS:
		{
			ModelParser.updateAppModelFields();
			return state;
		}
		case UPDATE_PRODUCTS_POST_THOR_CHECK:
		{
			ModelParser.updateAppModelFieldsPostThorCheck();
			return state;
		}
		case FETCH_PDP_CCXI_DATA:
		{
			ModelParser.fillPDPModel(action.payload.PDPJson, action.payload.hasThor, action.payload.inputLocale, action.payload.resolve, action.payload.reject);
			return state;
		}
		case FETCH_CATALOG_BANNERS_DATA:
		{
			const output = {
				allAppsBanner: action.payload.allAppsBanner ? action.payload.allAppsBanner.data : null,
				allAppsBannerDesktop: action.payload.allAppsBannerDesktop ? action.payload.allAppsBannerDesktop.data : null,
				allAppsBannerMobile: action.payload.allAppsBannerMobile ? action.payload.allAppsBannerMobile.data : null,
				allAppsBannerWeb: action.payload.allAppsBannerWeb ? action.payload.allAppsBannerWeb.data : null
			};
			if (output.allAppsBanner) {
				ModelParser.fillAllAppBannerModel(output.allAppsBanner);
			}
			else {
				ModelParser.fillAllAppBannerModel(output.allAppsBannerDesktop && output.allAppsBannerDesktop.data.appsBannerByPath_2.item, 'desktop');
				ModelParser.fillAllAppBannerModel(output.allAppsBannerMobile && output.allAppsBannerMobile.data.appsBannerByPath_2.item, 'mobile');
				ModelParser.fillAllAppBannerModel(output.allAppsBannerWeb && output.allAppsBannerWeb.data.appsBannerByPath_2.item, 'web');
			}
			ModelParser.fillCategoriesBannerModel(null);
			return state;
		}
		case FETCH_SINGLE_APP_PLAN_CVS_DATA:
		{
			const output = {};
			const appPickerContentfulData = action.payload.appPickerContentfulData ? action.payload.appPickerContentfulData.data : null;
			if (appPickerContentfulData === null || appsUtil.hasFeature('use_nuj_singleapp_fallback')) {
				// fill with local copy of json
				const appPickerFallbackData = action.payload.appPickerFallbackData && action.payload.appPickerFallbackData.data;
				appsUtil.isCVSAppPickerDataFallback = true;
				appPickerFallbackData && AppPickerUtil.init(appPickerFallbackData, action.payload.inputLocale);
				output.appPickerContentfulData = appPickerFallbackData;
			}
			else {
				AppPickerUtil.init(appPickerContentfulData, action.payload.inputLocale);
				output.appPickerContentfulData = appPickerContentfulData;
			}
			if (!output.appPickerContentfulData) {
				output.errorType = '500';
			}
			return Object.assign({}, state, output);
		}
		case FETCH_DATA_FOR_APP_PICKER:
		{
			const output = {};
			output.hasThor = action.payload.hasThor;
			appsUtil.hasThor = output.hasThor;
			const appPickerContentfulData = action.payload.appPickerContentfulData ? action.payload.appPickerContentfulData.data : null;
			const intentData = action.payload.intentData ? action.payload.intentData.data : null;
			if (appPickerContentfulData === null || appsUtil.hasFeature('use_nuj_app_picker_fallback')) {
				// fill with local copy of json
				const appPickerFallbackData = action.payload.appPickerFallbackData && action.payload.appPickerFallbackData.data;
				appsUtil.isCVSAppPickerDataFallback = true;
				appPickerFallbackData && AppPickerUtil.init(appPickerFallbackData, action.payload.inputLocale, intentData);
				output.appPickerContentfulData = appPickerFallbackData;
			}
			else {
				AppPickerUtil.init(appPickerContentfulData, action.payload.inputLocale, intentData);
				output.appPickerContentfulData = appPickerContentfulData;
			}
			if (!output.appPickerContentfulData) {
				output.errorType = '500';
			}
			return Object.assign({}, state, output);
		}
		case FETCH_FFC_APP_PICKER_DATA:
		{
			let productArr;
			let servicesArr;
			let mobileAppArr;
			const productsList = {};
			const output = {};

			if (action.payload.ffcData && typeof action.payload.ffcData.data.channel != 'undefined') {
				const ccmChannel = action.payload.ffcData.data.channel.find(channel => channel.name === 'ccm');
				productArr = ccmChannel ? ccmChannel.products.product : undefined;
				output.cdnInfo = action.payload.ffcData.data.channel[0].cdn;

				const serviceChannel = action.payload.ffcData.data.channel.find(channel => channel.name === 'services');
				servicesArr = serviceChannel ? serviceChannel.services.service : undefined;

				const mobileAppChannel = action.payload.ffcData.data.channel.find(channel => channel.name === 'mobileApps');
				mobileAppArr = mobileAppChannel ? mobileAppChannel.mobileApps.mobileApp : undefined;
			}

			// create lineage map and sort
			output.appsFFCProductList = null;
			if (productArr) {
				productArr = appsUtil.sortFFCProductArray(productArr);
				for (const index in productArr) {
					productsList[productArr[index].id] = productArr[index];
					ModelParser.createApplineage(productArr[index], 'desktop');
				}
				output.appsFFCProductList = productsList;
			}

			if (servicesArr) {
				servicesArr = appsUtil.sortFFCProductArray(servicesArr);
				for (const index in servicesArr) {
					ModelParser.createApplineage(servicesArr[index], 'web');
				}
			}

			if (mobileAppArr) {
				mobileAppArr = appsUtil.sortFFCProductArray(mobileAppArr);
				for (const index in mobileAppArr) {
					ModelParser.createApplineage(mobileAppArr[index], 'mobile');
				}
			}

			appsUtil.init(output.appsFFCProductList, null, output.cdnInfo, null, null);
			appsUtil.isAppLineageMapInit = true;
			if (!output.appsFFCProductList) {
				output.errorType = '500';
			}

			return Object.assign({}, state, output);
		}
		case FETCH_FFC_ALL_VERSION_DATA:
		{
			const output = {};
			if (state.sapCodeDataFetchStatus) {
				output.sapCodeDataFetchStatus = state.sapCodeDataFetchStatus.concat(action.payload.sapCodeDataFetchStatus);
			}
			else {
				output.sapCodeDataFetchStatus = action.payload.sapCodeDataFetchStatus;
			}
			// If even one of the FFC call fails, it means 5xx error for us
			for (const sapCodeStatusObj of action.payload.sapCodeDataFetchStatus) {
				if (!sapCodeStatusObj.status) {
					output.errorType = '500';
				}
			}
			return Object.assign({}, state, output);
		}
		default:
			return state;
	}
};
